import { type MeasurementKind } from "../api/estimate.types.ts"

export type Point = [number, number]

// The resolution sheets are drawn at: an image pixel is 1/SHEET_DPI of an
// inch of paper, which is what turns a measurement into feet.
export const SHEET_DPI = 150

export interface ScalePreset {
  label: string
  feetPerInch: number
}

// The drawing scales plans are printed at: architectural (fractions of an
// inch to the foot) and engineering (feet to the inch).
export const SCALE_PRESETS: ScalePreset[] = [
  { label: '1/16" = 1\'-0"', feetPerInch: 16 },
  { label: '3/32" = 1\'-0"', feetPerInch: 32 / 3 },
  { label: '1/8" = 1\'-0"', feetPerInch: 8 },
  { label: '3/16" = 1\'-0"', feetPerInch: 16 / 3 },
  { label: '1/4" = 1\'-0"', feetPerInch: 4 },
  { label: '3/8" = 1\'-0"', feetPerInch: 8 / 3 },
  { label: '1/2" = 1\'-0"', feetPerInch: 2 },
  { label: '3/4" = 1\'-0"', feetPerInch: 4 / 3 },
  { label: '1" = 1\'-0"', feetPerInch: 1 },
  { label: '1-1/2" = 1\'-0"', feetPerInch: 2 / 3 },
  { label: '3" = 1\'-0"', feetPerInch: 1 / 3 },
  { label: "1\" = 10'", feetPerInch: 10 },
  { label: "1\" = 20'", feetPerInch: 20 },
  { label: "1\" = 30'", feetPerInch: 30 },
  { label: "1\" = 40'", feetPerInch: 40 },
  { label: "1\" = 50'", feetPerInch: 50 },
  { label: "1\" = 100'", feetPerInch: 100 }
]

export const UNIT_FOR: Record<MeasurementKind, string> = {
  length: "lf",
  area: "sf",
  count: "ea"
}

const distance = (a: Point, b: Point) => Math.hypot(b[0] - a[0], b[1] - a[1])

export function pathLength(points: Point[]): number {
  let total = 0
  for (let index = 1; index < points.length; index += 1)
    total += distance(points[index - 1], points[index])
  return total
}

export function polygonArea(points: Point[]): number {
  if (points.length < 3) return 0
  let twice = 0
  points.forEach((point, index) => {
    const next = points[(index + 1) % points.length]
    twice += point[0] * next[1] - next[0] * point[1]
  })
  return Math.abs(twice) / 2
}

// A preview of what the server will store, from points in paper inches.
export function measuredValue(
  kind: MeasurementKind,
  points: Point[],
  feetPerInch: number | null
): number | null {
  if (kind === "count") return points.length
  if (!feetPerInch) return null
  if (kind === "length") return pathLength(points) * feetPerInch
  return polygonArea(points) * feetPerInch * feetPerInch
}

export const enoughPoints = (kind: MeasurementKind, points: Point[]) =>
  points.length >= (kind === "area" ? 3 : kind === "length" ? 2 : 1)

export function formatQuantity(value: number, unit: string): string {
  const rounded = Math.round(value * 100) / 100
  return `${rounded.toLocaleString("en-US", { maximumFractionDigits: 2 })} ${unit}`
}

// 12' 6", 12'6, 12 6, 12.5, 12 ft 6 in → feet. Null for anything else.
export function parseFeetInches(text: string): number | null {
  const cleaned = text
    .trim()
    .toLowerCase()
    .replace(/feet|foot|ft/g, "'")
    .replace(/inches|inch|in/g, '"')
  if (!cleaned) return null
  const plain = /^\d+(\.\d+)?$/.exec(cleaned)
  if (plain) return Number(cleaned)
  const match = /^(\d+(?:\.\d+)?)\s*'?\s*-?\s*(\d+(?:\.\d+)?)?\s*"?$/.exec(
    cleaned
  )
  if (!match || (!cleaned.includes("'") && !match[2])) return null
  const feet = Number(match[1])
  const inches = match[2] ? Number(match[2]) : 0
  if (inches >= 12) return null
  return feet + inches / 12
}

export function formatFeetInches(feet: number): string {
  let whole = Math.floor(feet)
  let inches = Math.round((feet - whole) * 12)
  if (inches === 12) {
    whole += 1
    inches = 0
  }
  return `${whole}' ${inches}"`
}

// Holding Shift keeps a segment straight across or straight down.
export function constrain(
  previous: Point | undefined,
  point: Point,
  straight: boolean
): Point {
  if (!straight || !previous) return point
  return Math.abs(point[0] - previous[0]) >= Math.abs(point[1] - previous[1])
    ? [point[0], previous[1]]
    : [previous[0], point[1]]
}
